import 'client-only';

import { logger } from '@curology/logger/client';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import {
  type QuestionCreatedEvent,
  SurveyModel,
  type PageModel,
} from 'survey-core';
import {
  type AfterRenderQuestionEvent,
  type CurrentPageChangingEvent,
} from 'survey-core/typings/survey-events-api';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import COOKIE_KEYS from '@/consts/cookieKeys';
import { TreatmentCategoryIds } from '@/consts/treatmentCategories';
import { getEventFlowValueFromVisitType } from '@/helpers/visitUtils';
import { useLocationHash } from '@/hooks/useLocationHash';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { type SurveyJsResource } from '@/services/funnelService/types';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';
import cookieStorage from '@/utils/cookies/client';
import { type HttpError } from '@/utils/http/client';

// Contract with backend SurveyResponsesController — keep in sync.
const INVALID_RESPONSE_ENCODING_ERROR = 'invalid_response_data' as const;

import { boot } from '../config';
import { squeeze } from '../config/theme';
import { addTimestampToAnswers } from '../listeners/questions';
import { handleMarkdown, saveResponses } from '../listeners/surveys';
import { obfuscatePageNumber } from '../utils/obfuscatePageNumber';

const IS_INFORMATION_ACCURATE_TYPE = 'is-information-accurate';

/**
 * PRODSUP-496: keep the patient on the "Is the info in your medical chart still accurate?" gate until
 * it's answered, so a resumed/stale-cached session can't land them past it and skip the chart pre-fill.
 *
 * Snaps BACKWARD only: we move to the gate solely when it sits *before* the current page, so we never
 * pull the patient forward over steps that precede the gate (keeps this correct even if the questionnaire
 * is ever reordered so the gate isn't the first question). An invisible gate page (e.g. if it ever gains
 * a `visibleIf`) isn't in `visiblePages`, so enforcement disables itself rather than silently targeting
 * an unreachable page. No-op unless the survey contains an `is-information-accurate` question, so only
 * the medical-history-update flow is affected.
 */
const enforceAccuracyGate = (survey: SurveyModel) => {
  const gate = survey
    .getAllQuestions()
    .find((question) => question.getType() === IS_INFORMATION_ACCURATE_TYPE);

  if (!gate?.isEmpty() || !gate.page) {
    return;
  }

  // indexOf returns -1 when the gate's page isn't visible (e.g. a future `visibleIf`), which disables
  // enforcement rather than targeting an unreachable page. visiblePages indices align with currentPageNo.
  const gateIndex = survey.visiblePages.indexOf(gate.page as PageModel);
  if (gateIndex >= 0 && gateIndex < survey.currentPageNo) {
    survey.currentPageNo = gateIndex;
  }
};

const useSurvey = (data: SurveyJsResource) => {
  boot();
  const surveyModel = new SurveyModel();
  surveyModel.setVariable('user', data.previousResponses);
  surveyModel.goNextPageAutomatic = false;
  surveyModel.textUpdateMode = 'onTyping';
  const [model] = useState<SurveyModel>(surveyModel);
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [hideNavigation, setHideNavigation] = useState(false);
  const [hideBackNavigation, setHideBackNavigation] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [disclaimer, setDisclaimer] = useState<string | undefined>();
  const { hash, setHash } = useLocationHash();
  const { treatmentCategory, brand } = useFlowContext();
  const { error: flashError } = useFlashMessages();
  const { visit } = useVisitContext();
  const { handleIfSessionExpired } = useSessionExpired(brand);

  const save = useMutation({
    mutationFn: () =>
      saveResponses(model, data.provider, data.id, brand, visit),

    onError: (error: HttpError) => {
      if (handleIfSessionExpired(error.response?.status)) return;
      // model.clear(false, false): reset SurveyJS from "completed" back to "running"
      // state without clearing the patient's answers or jumping to the first page.
      // Without this, the Survey component renders the completion UI even after
      // save.reset(), leaving the user unable to retry.
      model.clear(false, false);
      model.currentPageNo = model.pageCount - 1;
      save.reset();
      const errorData = error.response?.data as
        | { error?: unknown; questions?: unknown }
        | undefined;
      if (
        error.response?.status === 422 &&
        typeof errorData?.error === 'string' &&
        errorData.error === INVALID_RESPONSE_ENCODING_ERROR
      ) {
        const questions = Array.isArray(errorData.questions)
          ? (errorData.questions as string[]).map((q) =>
              q
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;'),
            )
          : [];
        const questionList =
          questions.length > 0 ? ` (${questions.join(', ')})` : '';
        flashError({
          message: `Some of your answers${questionList} contain special characters that could not be saved. Please re-enter them and try again.`,
        });
      }
      logger.log(`Save Survey Responses Failed: ${error.message}`);
    },
  });

  useEffect(() => {
    setTimeout(() => {
      setIsReady(true);
    });
  }, []);

  const onCompleted = useCallback(() => {
    trackEvent(AnalyticsEvent.SURVEY_COMPLETED, {
      flow: getEventFlowValueFromVisitType(visit),
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
      group: data.group,
      surveyEngine: data.provider,
      treatmentCategories: treatmentCategory!,
      treatmentCategoryIds: TreatmentCategoryIds[treatmentCategory!],
      version: data.version,
    });
  }, [data.group, data.provider, data.version, treatmentCategory, visit]);

  const onCompletedSave = useCallback(() => {
    save.mutate();
  }, [save]);

  const onCurrentPageChanging = (
    survey: SurveyModel,
    event: CurrentPageChangingEvent,
  ) => {
    const { oldCurrentPage, isGoingBackward } = event;
    if (!oldCurrentPage || isGoingBackward) {
      return;
    }
    // Quiz Rearch: Note that this makes the assumption that
    // there is only one question per page. If we ever have a
    // page with multiple questions, we will need to update this.
    trackEvent(AnalyticsEvent.QUESTION_ANSWERED, {
      question: oldCurrentPage.questions[0].title,
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
      skipped: !(oldCurrentPage.questions[0]?.isAnswered || false),
    });
  };

  // We wrap callback registration within a useEffect to
  // ensure we don't register duplicate callbacks on re-renders.
  useEffect(() => {
    model.onComplete.clear();
    model.onComplete.add(onCompleted);
    model.onComplete.add(onCompletedSave);
    model.onCurrentPageChanging.clear();
    model.onCurrentPageChanging.add(onCurrentPageChanging);

    // Something here is not referentially safe, but we don't want this
    // to execute more than once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentCategory]);

  useEffect(() => {
    model.setVariable('visit', visit);
  }, [model, visit]);

  const onQuestionCreated = (
    sender: SurveyModel,
    options: QuestionCreatedEvent,
  ) => {
    const questionType = options.question.getType();
    if (questionType === 'radiogroup') {
      options.question.renderAs = 'cur-input-radiogroup';
      options.question.surveyModel = sender;
    } else if (questionType === 'checkbox') {
      options.question.renderAs = 'cur-input-checkbox';
      options.question.surveyModel = sender;
    } else if (questionType === 'imagepicker') {
      options.question.renderAs = 'cur-input-imagepicker';
      options.question.surveyModel = sender;
    }

    if (
      questionType === 'imagepicker' ||
      questionType === 'phone-number-verification' ||
      questionType === 'photo-upload' ||
      questionType === 'is-information-accurate' ||
      questionType === 'previous-formula'
    ) {
      options.question.surveyModel = sender;
    }
  };

  const onAfterRenderQuestion = (
    survey: SurveyModel,
    event: AfterRenderQuestionEvent,
  ) => {
    const { question, htmlElement } = event;
    trackEvent(AnalyticsEvent.QUESTION_DISPLAYED, {
      question: question.title,
      funnelId: cookieStorage.get(COOKIE_KEYS.funnelId) ?? '',
    });
    if (survey.isFirstPage && !!question.titleHighlight) {
      const questionTitleHTML = htmlElement.querySelector(
        '.sd-question__title--required span',
      );

      if (!questionTitleHTML) {
        return;
      }

      if (questionTitleHTML.textContent?.includes(question.titleHighlight)) {
        return;
      }

      const parentNode = questionTitleHTML.parentNode;
      if (!parentNode) {
        return;
      }

      const titleHighlightSpan = document.createElement('span');
      titleHighlightSpan.innerHTML = `
          <span class='cur-text-brand'>${question.titleHighlight}</span>
        `;

      parentNode.prepend(titleHighlightSpan);
    }
  };

  useEffect(() => {
    if (!model.onValueChanged.hasFunc(addTimestampToAnswers)) {
      model.onValueChanged.add(addTimestampToAnswers);
    }
    if (!model.onQuestionCreated.hasFunc(onQuestionCreated)) {
      model.onQuestionCreated.add(onQuestionCreated);
    }
    if (!model.onAfterRenderQuestion.hasFunc(onAfterRenderQuestion)) {
      model.onAfterRenderQuestion.add(onAfterRenderQuestion);
    }
    if (!model.onTextMarkdown.hasFunc(handleMarkdown)) {
      model.onTextMarkdown.add(handleMarkdown);
    }

    model.css = squeeze;
  }, [model]);

  // Navigation-related callbacks utilize the hash to navigate between
  // pages if the user is using the browser's forward/back buttons.
  useEffect(() => {
    // Reset the hash when the component mounts. This will
    // prevent us from navigating past pages that aren't completed.
    setHash('');

    model.onCurrentPageChanged.add((survey) => {
      setIsFirstPage(survey.isFirstPage);
      enforceAccuracyGate(survey);
    });

    model.onCurrentPageChanging.add(
      (
        survey: SurveyModel,
        { newCurrentPage }: { newCurrentPage: PageModel },
      ) => {
        if (newCurrentPage.num !== -1) {
          setHash(obfuscatePageNumber(newCurrentPage));
        }
        // @ts-expect-error Interface type
        if (newCurrentPage.elements[0].hideNavigation) {
          setHideNavigation(true);
        } else {
          setHideNavigation(false);
        }
        // @ts-expect-error Interface type
        if (newCurrentPage.elements[0].hideBackNavigation) {
          setHideBackNavigation(true);
        } else {
          setHideBackNavigation(false);
        }
      },
    );
    // Something here is not referentially safe, but we don't want this
    // to execute more than needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const page = model.pages.find(
      (page) => obfuscatePageNumber(page) === hash.slice(1),
    );
    if (page) {
      model.currentPage = page;
    } else {
      model.currentPageNo = 0;
    }

    setDisclaimer(model?.currentPage?.questions[0]?.disclaimer);
  }, [model, hash]);

  useEffect(() => {
    model.fromJSON(data.metadata.schema);
  }, [model, data.metadata.schema]);

  useEffect(() => {
    model.data = data.state;

    // If we have provided the survey with a state, we need to change
    // the current page to match the most recently answered question.
    if (data.state && Object.keys(data.state).length > 0) {
      model.currentPageNo = model.visiblePageCount - 1;
    }

    enforceAccuracyGate(model);
  }, [model, data.state]);

  return {
    model,
    isFirstPage,
    isReady,
    isSaving: save.isPending,
    isSaved: save.isSuccess,
    hasSaveError: save.isError,
    hideNavigation,
    hideBackNavigation,
    disclaimer,
  };
};

export { useSurvey };
